import { capitalize, isEmpty, isNil } from 'lodash-es'
import moment from 'moment'
import pluralize from 'pluralize'
import prettyBytes from 'pretty-bytes'
import { nextTick } from 'vue'

import { baseUrl } from '@/../app.config'

export const DECIMAL_NUMBER_PATTERN = '^(?=.)([+-]?([0-9]*)([.]*([0-9]+))?)$'
export const SCIENTIFIC_NUMBER_PATTERN =
  '^[+-]?[0-9]+(?:[.]{0,1}[0-9]*(?:[eE][+-]?[0-9]+)?)?$'
export const UUID_PATTERN =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const UUID_REGEX = new RegExp(UUID_PATTERN)

export const ID_URL_REGEX = new RegExp(
  `^${baseUrl}([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$`,
)

export const LOOL_MIMETYPES = [
  // WORD
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  'application/vnd.ms-word.document.macroEnabled.12',
  'application/vnd.ms-word.template.macroEnabled.12',
  // EXCEL
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-excel.template.macroEnabled.12',
  'application/vnd.ms-excel.addin.macroEnabled.12',
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  // POWER_POINT
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.template',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
]

const alphaNumCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})
export const alphaNumSort = (a, b) => alphaNumCollator.compare(a, b)

/**
 * Validates if a filename contains invalid characters for file systems.
 * Invalid characters include: : * ? " < > | and spaces
 * @param {string} filename - The filename to validate
 * @returns {boolean} - True if filename contains invalid characters, false otherwise
 */
export function hasInvalidFilenameChars(filename) {
  const invalidChars = /[:*?"<>|\s]/
  return invalidChars.test(filename)
}

export function getModelIconClasses(app, name) {
  switch (app) {
    case 'vocabularies':
    case 'annotations':
      return ['fa-solid', 'fa-font']
    case 'data_management':
      return ['fa-solid', 'fa-database']
    case 'workflows':
      return ['fa-solid', 'fa-sitemap']
  }

  switch (name) {
    case 'consumables':
      return ['fa-solid', 'fa-flask']
    case 'specimen':
      return ['fa-solid', 'fa-bug']
    case 'equipment':
      return ['fa-solid', 'fa-plug']
    case 'storageequipment':
      return ['fa-solid', 'fa-suitcase']
    case 'orders':
    case 'ordereditems':
      return ['fa-solid', 'fa-shopping-cart']
    case 'protocols':
      return ['fa-solid', 'fa-mortar-pestle']
    case 'samples':
      return ['fa-solid', 'fa-vial']
    case 'protocollists':
      return ['fa-solid', 'fa-code-branch']
    case 'assays':
    case 'instrumentruns':
      return ['fa-solid', 'fa-thermometer-half']
    case 'experiments':
      return ['fa-solid', 'fa-file-signature']
    case 'workflows':
      return ['fa-solid', 'fa-sitemap']
    case 'instrumentmodels':
    case 'studies':
      return ['fa-solid', 'fa-microscope']
    case 'projects':
      return ['fa-solid', 'fa-project-diagram']
    default:
      return null
  }
}

export function matchFileExtensionToIconClasses(ext) {
  const imageIconClasses = ['fa-solid', 'fa-file-image']
  // const imageIconClasses = ['fa-solid', 'file-image']
  switch (ext) {
    case '.txt':
    case '.log':
      return ['fa-solid', 'fa-file-lines']
    case '.csv':
    case '.tab':
    case '.tsv':
      return ['fa-solid', 'fa-file-csv']
    case '.zip':
    case '.tar':
    case '.gz':
      return ['fa-solid', 'fa-file-archive']
    case '.ppt':
    case '.pptx':
      return ['fa-solid', 'fa-file-powerpoint']
    case '.odt':
    case '.doc':
    case '.docx':
      return ['fa-solid', 'fa-file-word']
    case '.html':
      return ['fa-solid', 'fa-file-code']
    case '.pdf':
      return ['fa-solid', 'fa-file-pdf']
    case '.xls':
    case '.xlsx':
      return ['fa-solid', 'fa-file-excel']
    case '.jpg':
    case '.jpeg':
    case '.png':
    case '.bmp':
    case '.svg':
    case '.gif':
      return imageIconClasses
    default:
      return null
  }
}

export function getHeadingNameLabel(modelName) {
  modelName = modelName.toLowerCase()
  if (modelName === 'storageequipment') {
    modelName = 'Storage Equipment'
  } else if (modelName === 'samples') {
    modelName = 'Biomaterial'
  } else if (modelName === 'ontologyterm') {
    modelName = 'Ontology Term'
  } else {
    modelName = capitalize(modelName)
  }
  return modelName
}

export function getHeadingTypeLabel(modelType) {
  modelType = modelType.toLowerCase()
  if (modelType === 'total') {
    modelType = 'all'
  }
  return modelType
}

// TODO should merge graph functions
export function getModelGraphNodeShape(modelName) {
  switch (modelName) {
    case 'specimen':
      return 'rectangle'
    case 'assay':
      return 'triangle'
    case 'dataset':
      return 'pentagon'
    case 'experiment':
      return 'diamond'
    default:
      return 'ellipse'
  }
}

export function getModelGraphNodeColor(modelName) {
  switch (modelName) {
    case 'specimen':
      return '#F6AD55'
    case 'assay':
      return '#63B3ED'
    case 'dataset':
      return '#CBD5E0'
    case 'experiment':
      return '#DD7878'
    default:
      return '#68D391'
  }
}

export function getStatusIconClasses(status) {
  switch (status) {
    case 'danger':
      return 'fa-solid fa-skull'
    case 'warning':
      return 'fa-solid fa-exclamation-triangle'
    case 'info':
      return 'fa-solid fa-info-circle'
    case 'success':
      return 'fa-solid fa-check-circle'
    default:
      return ['fa-solid', 'fa-question-circle']
  }
}

export function modelNameSingularize(name) {
  switch (name) {
    case 'specimen':
      return name
    default:
      return pluralize(name, 1)
  }
}

export function modelNamePluralize(name) {
  switch (name) {
    case 'storageequipment':
      return name
    case 'ontologyterm':
      return 'ontologyterms'
    default:
      return pluralize(name)
  }
}

export function mapModelType(type) {
  if (!type) return 'DEFAULT'
  return type.toUpperCase()
}

/**
 * Formats a menu subsection object into a human-readable model label.
 *
 * Handles sections where the label alone is misleading without context
 * (e.g. assays, protocols) and the "All" aggregate type.
 *
 * @param {object} sub - currentSubsection from useMenu()
 * @param {boolean} [options.plural=true] - whether to return the plural form
 */
export function formatModelLabel(sub, { plural = true } = {}) {
  if (!sub?.label) return ''
  if (sub.label === 'All') return sub.sectionLabel

  if (sub.section === 'assays' && !sub.label.endsWith('Assay')) {
    return plural ? `${sub.label} Assays` : `${sub.label} Assay`
  }
  if (sub.section === 'protocols' && !sub.label.endsWith('Protocol')) {
    return plural ? `${sub.label} Protocols` : `${sub.label} Protocol`
  }

  return plural ? pluralize(sub.label) : sub.label
}

export function linkifyInternalUuid(str) {
  // Match UUIDs that are isolated (not part of URLs or other content):
  // - Not already inside anchor tags
  // - Preceded by whitespace, start of string, or certain punctuation (but not URL chars like / = & ?)
  // - Followed by whitespace, end of string, or certain punctuation (but not URL chars)
  const regex = new RegExp(
    `(?<!<a[^>]*?>[^<]*)(?<=[\\s,;:()\\[\\]{}|<>]|^)(${UUID_PATTERN})(?=[\\s,;:()\\[\\]{}|<>]|$)(?![^<]*?</a>)`,
    'gi',
  )
  return str.replace(
    regex,
    (id) =>
      `<a href="${baseUrl}${id}" target="_blank" rel="noopener">${id}</a>`,
  )
}

// TODO needs tests
export function hasValue(value) {
  return value !== '' && !isEmpty(value) && !isNil(value)
}

/**
 * Strip HTML tags from text while preserving plain text characters.
 * Use this for plain text fields where HTML is not expected.
 * For HTML content sanitization (with entity encoding), use sanitizeHtml() instead.
 *
 * @param {string} str - The input string that may contain HTML tags
 * @returns {string} - Plain text string with HTML tags removed
 */
export function stripHtmlTags(str) {
  if (!str) return ''
  // Template contents are inert, so parsing cannot fire resource event handlers.
  const template = document.createElement('template')
  template.innerHTML = str
  return template.content.textContent || ''
}

export function splitExt(fileName) {
  // no extension
  if (fileName.indexOf('.') === -1) {
    return [fileName, '']
  }
  // only one, leading dot, e.g. `.bashrc`
  if (/^\.[^.]+$/.test(fileName)) {
    return [fileName, '']
  }
  const re = /(.*)(\..+)/
  const [, root, ext] = re.exec(fileName)
  return [root, ext]
}

// OLS IRIs are often served as http:// — normalise to https:// so browsers
// don't log HTTPS-First Mode upgrade warnings in the console.
export function toHttps(url) {
  if (!url) return url
  return url.startsWith('http://') ? 'https://' + url.slice(7) : url
}

export function generateEmbedFileName(original) {
  const [root, ext] = splitExt(original.name)
  const now = moment()
  const timestamp = now.format('YYMMDDHHmmss')
  return `${root}_embed${timestamp}${ext}`
}

export const allowedEmbedFileTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
]

export function getWindowSize() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

export function getBreakpointInfo(width) {
  // get breakpoints programmatically from bulma
  const breakpoints = ['mobile', 'tablet', 'desktop', 'widescreen', 'fullhd']
  let current
  if (width <= 768) {
    current = breakpoints[0]
  } else if (width <= 1023) {
    current = breakpoints[1]
  } else if (width <= 1215) {
    current = breakpoints[2]
  } else if (width <= 1407) {
    current = breakpoints[3]
  } else {
    current = breakpoints[4]
  }
  const currentIndex = breakpoints.indexOf(current)
  return {
    current,
    lower: breakpoints.slice(0, currentIndex),
    higher: breakpoints.slice(currentIndex + 1),
  }
}

export function momentDate(date) {
  return date ? moment(date) : null
}

export function dateTimeNow() {
  const date = new Date()
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toJSON()
}

export function matchUuid(potentialUuid) {
  return UUID_REGEX.test(potentialUuid)
}

export const isUUID = (value) => {
  return value && matchUuid(value) && value?.length === 36
}

export function filterKeepUuids(arr) {
  return arr.filter((el) => matchUuid(el))
}

export function matchUuidSubstrings(str) {
  return str.match(UUID_REGEX) || []
}

export function commonPrefix(strArr) {
  let prefix = ''
  const strArrLen = strArr.length
  if (!strArrLen) return prefix
  else if (strArrLen === 1) return strArr[0]
  else {
    strArr.sort()
    let prefixLen = 0
    const firstItem = strArr[0]
    const lastItem = strArr.pop()
    const firstItemLen = firstItem.length
    while (prefixLen < firstItemLen) {
      const char1 = firstItem[prefixLen]
      const char2 = lastItem[prefixLen]
      if (char1 !== char2) break
      prefixLen++
    }
    prefix = firstItem.substr(0, prefixLen)
  }
  return prefix
}

export function stripLastOccurence(s, substr) {
  var n = s.toLowerCase().lastIndexOf(substr.toLowerCase())
  var pat = new RegExp(substr, 'i')
  return s.slice(0, n) + s.slice(n).replace(pat, '')
}

const stripCharRegex = /(^[\s_.]+|[\s_.]+$)/g
export function stripLeadingAndTrailingChars(str) {
  stripCharRegex.lastIndex = 0
  return str?.replace(stripCharRegex, '') || str
}

export function stripMask(str, mask = '') {
  if (mask.length) return str.replace(mask, '')
  else return str
}

export function trimExtensions(str, extList = []) {
  return extList.reduce((trimmedStr, ext) => {
    var reExt = new RegExp(`.${ext}$`, 'i')
    return trimmedStr.replace(reExt, '')
  }, str)
}

export function trimZipExtensions(str, extList = ['zip', 'gz']) {
  return trimExtensions(str, extList)
}

/**
 * Normalize an extension pattern to a consistent format with leading dot
 * @param  {string} pattern - The pattern to normalize (e.g., "*.jpg", ".jpg", "jpg")
 * @return {string|null} - Normalized extension with leading dot (e.g., ".jpg") or null if no pattern
 *
 * @example
 *    normalizeExtensionPattern('*.jpg') => ".jpg"
 *    normalizeExtensionPattern('.jpg') => ".jpg"
 *    normalizeExtensionPattern('jpg') => ".jpg"
 *    normalizeExtensionPattern('') => null
 */
export function normalizeExtensionPattern(pattern) {
  const trimmed = pattern?.trim()
  if (!trimmed) return null

  // Match "*.ext", ".ext", or "ext" and extract the extension part
  const match = trimmed.match(/^\*?\.?(.+)$/)
  return match ? `.${match[1]}` : null
}

export function splitStringAtLastDelimiter(str, delimiter) {
  let splittedStr
  const lastIndex = str.lastIndexOf(delimiter)
  if (lastIndex === -1) splittedStr = [str, '']
  else
    splittedStr = [
      str.substring(0, lastIndex),
      str.substring(lastIndex + delimiter.length),
    ]
  return splittedStr
}

/**
 * @param  {number} sizeBytes - number of bytes
 * @param  {Object} [options] - options to get locale, bits, signed, etc.
 * @return {string} - pretty bytes string
 *
 * @example
 *    prettySize(1900) => "1.9 kB"
 *    prettySize(1337, { bits: true }) => "1.34 kbit"
 */
export function prettySize(sizeBytes, options = {}) {
  return prettyBytes(sizeBytes, options)
}

export function uuidv4() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16),
  )
}

export const updateUniqueArray = (arr, newValue, mode = 'add') => {
  const set = new Set(arr)
  set[mode](newValue)
  return Array.from(set)
}

export const isAbsolutePath = (path) => {
  var isAbsolute = new RegExp('^([a-z]+:/|/)', 'i')
  return isAbsolute.test(path)
}

export const arrayToObjectById = (array) => {
  return array.reduce((byId, item) => {
    if (item.id === undefined) item.id = uuidv4()
    return {
      ...byId,
      [item.id]: {
        ...item,
        id: item.id,
      },
    }
  }, {})
}

export const scrollToElement = (element, opts = {}) => {
  const defaultScrollOpts = {
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
  }
  const scrollOptions = { ...defaultScrollOpts, ...opts }
  nextTick(() => {
    element?.scrollIntoView(scrollOptions)
  })
}

export const removeTrailingSlash = (str) =>
  str.endsWith('/') ? str.slice(0, -1) : str

export const addTrailingSlash = (str) => (str.endsWith('/') ? str : `${str}/`)

const decodeURIPath = (uri) => {
  uri = uri.split('://')[1]
  uri = decodeURIComponent(uri)
  return uri
}

export const removeRootPath = (rootPath, path) => {
  rootPath = addTrailingSlash(rootPath)
  const pattern = new RegExp(`${rootPath}`)
  return path.replace(pattern, '')
}

export const removeRootPathFromUri = (rootPath, uri) => {
  let uriPath = decodeURIPath(uri)
  const newPath = removeRootPath(rootPath, uriPath)
  return newPath
}

export const getUrlAndParams = (url) => {
  url = new URL(url)
  const urlPath = url.origin + url.pathname
  return { baseUrl: urlPath, urlParams: url.searchParams }
}

export function extractStringValue(value) {
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return value
  if (typeof value === 'object')
    return value.id ?? value.label ?? value.name ?? null
  return value
}

export function truncateText(text, maxLength) {
  if (!text) return ''
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

/**
 * Extract a human-readable message from an Axios error, trying the field names
 * the API uses (message / error / detail) before falling back.
 */
export function getErrorMessage(error, fallback = 'Something went wrong') {
  const data = error?.response?.data
  return (
    data?.message || data?.error || data?.detail || error?.message || fallback
  )
}

export function hasSomeMarkdownPatterns(text) {
  // Check for common Markdown syntax patterns
  // Note: This is a heuristic check, not a definitive markdown validator
  const markdownPatterns = [
    /^#{1,6}\s/, // Headers at start of line
    /^[*\-+]\s/, // Bullet points at start of line
    /\*\*[^*]+\*\*/, // Bold text
    /__[^_]+__/, // Bold text (alternative)
    /\*[^*]+\*/, // Italic text
    /_[^_]+_/, // Italic text (alternative)
    /\[.*?\]\(.*?\)/, // Links
    /`[^`]+`/, // Inline code
    /```[\s\S]*?```/, // Code blocks
    /!\[.*?\]\(.*?\)/, // Images
    /^>\s/, // Blockquotes at start of line
    /\n\s*\n/, // Paragraph breaks
  ]

  return markdownPatterns.some((pattern) => pattern.test(text))
}

export function formatAndTruncateText(text, options = {}) {
  const { maxLength = 200, showFull = false, DOMPurify, marked } = options

  if (!text) return ''

  // Helper function to truncate text at word boundary
  const truncateAtWordBoundary = (str, max) => {
    if (str.length <= max) return str
    const truncated = str.substring(0, max)
    const lastSpace = truncated.lastIndexOf(' ')
    return lastSpace > 0
      ? truncated.substring(0, lastSpace) + '...'
      : truncated + '...'
  }

  // If showFull is true, return the full formatted text
  if (showFull) {
    if (text.includes('<') && text.includes('>')) {
      return DOMPurify ? DOMPurify.sanitize(text) : text
    } else if (hasSomeMarkdownPatterns(text) && marked) {
      return DOMPurify
        ? DOMPurify.sanitize(marked.parse(text))
        : marked.parse(text)
    }
    return text
  }

  let formatted = text

  // Check if it looks like HTML
  if (text.includes('<') && text.includes('>')) {
    // Clean the HTML
    formatted = DOMPurify ? DOMPurify.sanitize(text) : text
    return truncateAtWordBoundary(formatted, maxLength)
  }

  // Check if it looks like Markdown
  if (hasSomeMarkdownPatterns(text) && marked) {
    // Convert markdown to HTML and clean it
    const parsed = marked.parse(text)
    formatted = DOMPurify ? DOMPurify.sanitize(parsed) : parsed
    return truncateAtWordBoundary(formatted, maxLength)
  }

  // Plain text
  return truncateAtWordBoundary(text, maxLength)
}
